export interface MessageTextParts {
  title: string;
  body: string;
}

const HEADING_PATTERN = /^# (.+)$/;
const ESCAPE_CHARACTER = '\\';
const ESCAPED_HEADING_PATTERN = /^\\+# .+$/;

function resolveFirstLine(text: string): string {
  const firstNewlineIndex = text.indexOf('\n');

  return firstNewlineIndex === -1 ? text : text.slice(0, firstNewlineIndex);
}

function needsHeadingEscape(body: string): boolean {
  const firstLine = resolveFirstLine(body.replace(/\r\n/g, '\n'));

  return (
    HEADING_PATTERN.test(firstLine) || ESCAPED_HEADING_PATTERN.test(firstLine)
  );
}

export function parseMessageText(
  text: string | null | undefined
): MessageTextParts {
  if (text === null || text === undefined || text === '') {
    return { title: '', body: '' };
  }

  const normalised = text.replace(/\r\n/g, '\n');
  const firstNewlineIndex = normalised.indexOf('\n');
  const firstLine = resolveFirstLine(normalised);

  if (ESCAPED_HEADING_PATTERN.test(firstLine)) {
    return { title: '', body: normalised.slice(ESCAPE_CHARACTER.length) };
  }

  const headingMatch = firstLine.match(HEADING_PATTERN);

  if (headingMatch === null) {
    return { title: '', body: normalised };
  }

  const rest =
    firstNewlineIndex === -1 ? '' : normalised.slice(firstNewlineIndex + 1);

  return {
    title: headingMatch[1],
    body: rest.replace(/^\n+/, ''),
  };
}

export function serializeMessageText(parts: MessageTextParts): string {
  if (parts.title.trim() === '') {
    return needsHeadingEscape(parts.body)
      ? `${ESCAPE_CHARACTER}${parts.body}`
      : parts.body;
  }

  if (parts.body === '') {
    return `# ${parts.title}`;
  }

  return `# ${parts.title}\n${parts.body}`;
}

export function plainTextToMessageText(
  text: string | null | undefined
): string {
  const normalised = (text ?? '').replace(/\r\n/g, '\n');
  const firstNewlineIndex = normalised.indexOf('\n');

  if (firstNewlineIndex === -1) {
    return serializeMessageText({ title: normalised, body: '' });
  }

  return serializeMessageText({
    title: normalised.slice(0, firstNewlineIndex),
    body: normalised.slice(firstNewlineIndex + 1),
  });
}

export function resolveMessageDisplayText(
  text: string | null | undefined
): string {
  const parts = parseMessageText(text);

  return parts.title !== '' ? parts.title : parts.body;
}

export function preserveMessageTitle(
  previousText: string | null | undefined,
  nextText: string
): string {
  const next = parseMessageText(nextText);

  if (next.title !== '') {
    return nextText;
  }

  const previousTitle = parseMessageText(previousText).title;

  if (previousTitle.trim() === '') {
    return nextText;
  }

  return serializeMessageText({ title: previousTitle, body: next.body });
}
